import React, { useState, useMemo } from "react";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { Skeleton } from "../../Components/Common/LoadingStates";
import { GaugeTile, CohortHeatmap } from "../../Components/Common/NorthStarViz";
import DailyTasksCard from "./DailyTasksCard";
import { KpiCard, MiniLineChart, fmt, fmtUSD, shortDate } from "../../Components/Common/DashboardWidgets";
import { useChartAnnotations, mapAnnotationsToSeries, AnnotationManager } from "../../Components/Common/ChartAnnotations";
import TrafficSources from "../../Components/Common/TrafficSources";
import {
  getDashboardOverview,
  getAppErrorsSummary,
  getWewSummary,
  getWewTrend,
  getWewPassConditions,
  getWewRetention,
  getWewCountermeasures,
} from "../../helpers/fakebackend_helper";
import { useCachedRequest } from "../../helpers/cachedFetch";

document.title = "Dashboard | BiorGov Admin";

// Horizontal bar list for ranked categories (used by the WEW countermeasures panels).
// Each row: label, primary value (count, drives the bar), optional `display`
// override for the printed value (e.g. "62%"), optional secondary text.
function HBarList({ rows, accent = "var(--bl-accent)" }) {
  const max = Math.max(...rows.map((r) => r.value || 0), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
      {rows.map((r) => {
        const pct = Math.max(2, (r.value / max) * 100);
        return (
          <div key={r.label}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4, fontSize: 13 }}>
              <span style={{ color: "var(--bl-text-strong)" }}>{r.label}</span>
              <span style={{ color: "var(--bl-text-soft)", fontVariantNumeric: "tabular-nums" }}>
                {r.display ?? r.value.toLocaleString()}
                {r.sub ? <span style={{ marginLeft: 8, color: "var(--bl-text-soft)" }}>· {r.sub}</span> : null}
              </span>
            </div>
            <div style={{ height: 6, background: "rgba(255,255,255,0.05)", borderRadius: 3, overflow: "hidden" }}>
              <div style={{
                width: `${pct}%`,
                height: "100%",
                background: r.color || accent,
                borderRadius: 3,
                transition: "width 240ms ease",
              }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function pct1(n) {
  if (n == null) return "—";
  const s = (Number(n) * 100).toFixed(1);
  return (s.endsWith(".0") ? s.slice(0, -2) : s) + "%";
}

// ─── North Star shared bits ───────────────────────────────────────────────

// Shown when a WEW endpoint isn't built yet (fetcher resolved to null) — keeps
// the section frame visible and explains the blank instead of erroring.
function AwaitingBackend({ note }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, padding: "1.5rem", color: "var(--bl-text-soft)", textAlign: "center" }}>
      <i className="ri-server-line" style={{ fontSize: 26, opacity: 0.5 }} />
      <span style={{ fontSize: 13 }}>Awaiting backend — no data yet</span>
      {note && <span style={{ fontSize: 11, opacity: 0.8 }}>{note}</span>}
    </div>
  );
}

function SectionCard({ title, right, children, style }) {
  return (
    <div className="bl-glass" style={{ padding: "1.25rem", ...style }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: "1rem", gap: 8 }}>
        <h3 style={{ margin: 0, fontFamily: "var(--bl-font-display)", fontSize: "1.05rem", color: "#fff", fontWeight: 600 }}>{title}</h3>
        {right != null && <span style={{ fontSize: 12, color: "var(--bl-text-soft)" }}>{right}</span>}
      </div>
      {children}
    </div>
  );
}

function DeltaChip({ current, prev }) {
  if (current == null || prev == null) return null;
  const delta = current - prev;
  if (delta === 0) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 700, color: "var(--bl-text-soft)", background: "rgba(255,255,255,0.07)", padding: "2px 8px", borderRadius: 6 }}>
        <i className="ri-subtract-line" />
        no change WoW
      </span>
    );
  }
  const pct = prev ? (delta / prev) * 100 : null;
  const up = delta > 0;
  const fg = up ? "var(--bl-success-soft, #2BD56F)" : "#ff8a98";
  const bg = up ? "rgba(0,205,82,0.14)" : "rgba(255,93,108,0.14)";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 700, color: fg, background: bg, padding: "2px 8px", borderRadius: 6 }}>
      <i className={up ? "ri-arrow-up-line" : "ri-arrow-down-line"} />
      {up ? "+" : ""}{delta}{pct != null ? ` (${pct >= 0 ? "+" : ""}${pct.toFixed(0)}%)` : ""}
    </span>
  );
}

const Overview = () => {
  const [showSnapshot, setShowSnapshot] = useState(true);

  // ── North Star (WEW) fetches. Each fetcher `.catch(() => null)` so an
  // unbuilt endpoint degrades to an "awaiting backend" empty state, never an
  // error. NOTE: axios interceptor already unwraps `.data` — read fields off
  // the response directly.
  const wewSummaryReq = useCachedRequest({ key: "wew:summary",         fetcher: () => getWewSummary().catch(() => null) });
  const wewTrendReq   = useCachedRequest({ key: "wew:trend:12",        fetcher: () => getWewTrend(12).catch(() => null) });
  const wewPassReq    = useCachedRequest({ key: "wew:pass",            fetcher: () => getWewPassConditions().catch(() => null) });
  const wewRetReq     = useCachedRequest({ key: "wew:retention:8",     fetcher: () => getWewRetention(8).catch(() => null) });
  const wewCmReq      = useCachedRequest({ key: "wew:countermeasures", fetcher: () => getWewCountermeasures().catch(() => null) });

  const wew   = wewSummaryReq.data;
  const wewTrend = wewTrendReq.data;
  const wewPass  = wewPassReq.data;
  const wewRet   = wewRetReq.data;
  const wewCm    = wewCmReq.data;

  const wewTrendPoints = useMemo(
    () => (wewTrend?.series || []).map((p) => ({ label: shortDate(p.week_start), y: Number(p.wew) || 0 })),
    [wewTrend],
  );

  // Event markers on the WEW trend (content drops, KOL posts, Spaces, releases).
  // The series is Monday-anchored weekly buckets, so an event resolves to its
  // WEEK, not its day — see mapAnnotationsToSeries.
  const { annotations, reload: reloadAnnotations } = useChartAnnotations("wew");
  const wewTrendMarks = useMemo(
    () => mapAnnotationsToSeries(annotations, wewTrend?.series || []),
    [annotations, wewTrend],
  );
  const cmBalancePoints = useMemo(
    () => (wewCm?.stable_balance_30d?.series || []).map((p) => ({ label: shortDate(p.date), y: Number(p.avg_balance_usd) || 0 })),
    [wewCm],
  );
  const cmStableUsdPoints = useMemo(
    () => (wewCm?.wew_stable_usd?.series || []).map((p) => ({ label: shortDate(p.week_start), y: Number(p.usd) || 0 })),
    [wewCm],
  );
  const cmSmileRows = useMemo(
    () => (wewCm?.smile_curve?.deciles || []).map((d) => {
      const pct = Math.round((Number(d.retained_pct) || 0) * 100);
      return { label: `D${d.decile}`, value: pct, display: `${pct}%` };
    }),
    [wewCm],
  );
  const cmTtfaRows = useMemo(
    () => (wewCm?.time_to_first_action?.buckets || []).map((b) => ({ label: b.label, value: Number(b.count) || 0 })),
    [wewCm],
  );

  // ── Overview snapshot fetch ─────────────────────────────────────────
  // Swap/fee/ramp metrics now live on the Money→Fees page; this page keeps
  // only the user/alert KPIs and the recent-activity feed.
  const combined = useCachedRequest({
    key: "overview:combined",
    fetcher: () => getDashboardOverview().catch(() => ({})),
  });
  const loading = combined.loading;
  const error = combined.error ? (typeof combined.error === "string" ? combined.error : combined.error?.message || "Failed to load") : null;
  const overview = combined.data || null;

  // App-errors summary. Best-effort: a 403 (no errors.view permission)
  // hides the card without breaking the page.
  const errors = useCachedRequest({
    key: "overview:errors-summary",
    fetcher: () => getAppErrorsSummary().then((res) => res || {}),
  });
  const errorsSummary = errors.data;
  const errorsAllowed = !errors.error;

  // ── Derived values ──────────────────────────────────────────────────
  const cardUsers    = overview?.total_users         ?? 0;
  const appUsers     = overview?.app_users           ?? 0;
  const appUsersSource = overview?.app_users_source ?? "direct";
  const activeAlerts = overview?.active_alerts       ?? 0;
  const uniqueVisitors = overview?.website_unique_visitors ?? 0;
  const stuckPending = overview?.stuck_pending_sends || { count: 0, threshold_min: 30 };

  return (
    <div style={{ padding: "clamp(1rem, 2.5vw, 1.75rem)", maxWidth: 1400, margin: "0 auto" }}>
      <BreadCrumb title="Dashboard" pageTitle="Dashboard" />

      {error && <div className="alert alert-danger" style={{ marginBottom: "1rem" }}>{error}</div>}

      {/* ════════ SECTION A — WEW headline + trend ════════ */}
      <div className="northstar-grid" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.4fr)", gap: "1rem", marginBottom: "1.5rem" }}>
        <div className="bl-glass" style={{ padding: "1.5rem", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ fontSize: 11, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
            North Star · Weekly Engaged Wallets
          </div>
          {wewSummaryReq.loading ? (
            <div style={{ marginTop: 10 }}><Skeleton height={48} width="55%" /></div>
          ) : wew == null ? (
            <AwaitingBackend note="GET /metrics/wew/summary" />
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginTop: 6, flexWrap: "wrap" }}>
                <span style={{ fontSize: "3rem", fontFamily: "var(--bl-font-numeric)", color: "#fff", fontWeight: 800, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                  {Number(wew.wew ?? 0).toLocaleString()}
                </span>
                <DeltaChip current={wew.wew} prev={wew.wew_prev} />
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: "var(--bl-text-soft)" }}>
                trailing {wew.window_days || 7} days · as of {shortDate(wew.as_of)}
              </div>
              <div style={{ marginTop: 6, fontSize: 13, color: "var(--bl-text-strong)" }}>
                {wew.installed_total != null ? (
                  <>
                    {pct1(wew.wew_installed_ratio)} of installs
                    <span style={{ color: "var(--bl-text-soft)" }}> · {Number(wew.wew ?? 0).toLocaleString()} WEW / {Number(wew.installed_total).toLocaleString()} installs</span>
                  </>
                ) : (
                  <span style={{ color: "var(--bl-text-soft)" }}>install total unavailable — sync App Metrics to see the WEW/installed ratio</span>
                )}
              </div>
              {/* swap/card-dominant split */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 14 }}>
                <div style={{ padding: "0.7rem 0.9rem", borderRadius: 10, background: "rgba(123,124,255,0.08)", border: "1px solid rgba(123,124,255,0.16)" }}>
                  <div style={{ fontSize: 10, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Swap-dominant</div>
                  <div style={{ fontSize: "1.3rem", fontFamily: "var(--bl-font-numeric)", color: "#fff", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{Number(wew.wew_swap_dominant ?? 0).toLocaleString()}</div>
                </div>
                <div style={{ padding: "0.7rem 0.9rem", borderRadius: 10, background: "rgba(0,205,82,0.06)", border: "1px solid rgba(0,205,82,0.16)" }}>
                  <div style={{ fontSize: 10, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Card-dominant</div>
                  <div style={{ fontSize: "1.3rem", fontFamily: "var(--bl-font-numeric)", color: "#fff", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{Number(wew.wew_card_dominant ?? 0).toLocaleString()}</div>
                </div>
              </div>
            </>
          )}
        </div>

        <SectionCard title="WEW trend" right="last 12 weeks">
          {wewTrendReq.loading ? (
            <Skeleton height={140} radius={8} />
          ) : wewTrend == null ? (
            <AwaitingBackend note="GET /metrics/wew/trend" />
          ) : wewTrendPoints.length === 0 ? (
            <div style={{ height: 140, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--bl-text-soft)" }}>No trend data yet</div>
          ) : (
            <>
              <MiniLineChart points={wewTrendPoints} annotations={wewTrendMarks} />
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 11, color: "var(--bl-text-soft)" }}>
                {(() => {
                  // Evenly sampled labels anchored to the first AND last point, so the
                  // space-between row lines up with the chart's actual x positions.
                  const n = wewTrendPoints.length;
                  const count = Math.min(6, n);
                  const idxs = count === 1 ? [0]
                    : Array.from({ length: count }, (_, k) => Math.round((k * (n - 1)) / (count - 1)));
                  return idxs.map((i) => <span key={i}>{wewTrendPoints[i].label}</span>);
                })()}
              </div>
              <AnnotationManager chartKey="wew" annotations={annotations} onChange={reloadAnnotations} />
            </>
          )}
        </SectionCard>
      </div>

      {/* ════════ Founder Daily Tasks (issue #98) — super-admin only, hides itself otherwise ════════ */}
      <DailyTasksCard />

      {/* ════════ Traffic sources — first-touch split of the unique-visitors card ════════ */}
      <SectionCard title="Traffic sources" right="biorlabs.com · first touch" style={{ marginBottom: "1.5rem" }}>
        <TrafficSources />
      </SectionCard>

      {/* ════════ SECTION B — 90-day pass conditions ════════ */}
      <SectionCard title="90-day commitment" right="current vs target" style={{ marginBottom: "1.5rem" }}>
        {wewPassReq.loading ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1rem" }}>
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={120} radius={12} />)}
          </div>
        ) : wewPass == null ? (
          <AwaitingBackend note="GET /metrics/wew/pass-conditions" />
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1rem" }}>
            {(wewPass.conditions || []).map((c) => (
              <GaugeTile key={c.id} label={c.label} current={c.current} target={c.target} unit={c.unit} status={c.status} alarmBelow={c.alarm_below} />
            ))}
          </div>
        )}
      </SectionCard>

      {/* ════════ SECTION D — W1 retention cohorts ════════ */}
      <SectionCard title="W1 retention by cohort" right="qualifying action in week N+1" style={{ marginBottom: "1.5rem" }}>
        {wewRetReq.loading ? (
          <Skeleton height={160} radius={8} />
        ) : wewRet == null ? (
          <AwaitingBackend note="GET /metrics/wew/retention-cohorts" />
        ) : (wewRet.cohorts || []).length === 0 ? (
          <div style={{ color: "var(--bl-text-soft)", fontSize: 14 }}>No cohort data yet</div>
        ) : (
          <CohortHeatmap cohorts={wewRet.cohorts} />
        )}
      </SectionCard>

      {/* ════════ SECTION E — Countermeasure tiles ════════ */}
      <div style={{ marginBottom: "2rem" }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: "0.85rem" }}>
          <h3 style={{ margin: 0, fontFamily: "var(--bl-font-display)", fontSize: "1.05rem", color: "#fff", fontWeight: 600 }}>Countermeasures</h3>
          <span style={{ fontSize: 12, color: "var(--bl-text-soft)" }}>anti-gaming / substitution checks</span>
        </div>
        {wewCmReq.loading ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1rem" }}>
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={150} radius={12} />)}
          </div>
        ) : wewCm == null ? (
          <div className="bl-glass" style={{ padding: "1.25rem" }}><AwaitingBackend note="GET /metrics/wew/countermeasures" /></div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1rem" }}>
            <SectionCard title="Stable balance / wallet" right="30d">
              {cmBalancePoints.length ? <MiniLineChart points={cmBalancePoints} color="var(--bl-success-soft, #2BD56F)" height={110} /> : <div style={{ color: "var(--bl-text-soft)", fontSize: 13 }}>No data</div>}
            </SectionCard>
            <SectionCard title="Power-user smile" right="retention by decile">
              {cmSmileRows.length ? <HBarList rows={cmSmileRows} /> : <div style={{ color: "var(--bl-text-soft)", fontSize: 13 }}>No data</div>}
            </SectionCard>
            <SectionCard title="Time to first action" right="signup → qualifying">
              {cmTtfaRows.length ? <HBarList rows={cmTtfaRows} /> : <div style={{ color: "var(--bl-text-soft)", fontSize: 13 }}>No data</div>}
            </SectionCard>
            <SectionCard title="WEW in stable USD" right="vs market beta">
              <div style={{ fontSize: "1.6rem", fontFamily: "var(--bl-font-numeric)", color: "#fff", fontWeight: 700, marginBottom: 6 }}>{fmtUSD(wewCm?.wew_stable_usd?.current)}</div>
              {cmStableUsdPoints.length ? <MiniLineChart points={cmStableUsdPoints} height={90} /> : null}
            </SectionCard>
          </div>
        )}
      </div>

      {/* ════════ SECTION F — Operational snapshot (legacy Overview, preserved) ════════ */}
      <div className="bl-glass" style={{ padding: 0, marginBottom: showSnapshot ? "1.5rem" : 0, overflow: "hidden" }}>
        <button
          type="button"
          onClick={() => setShowSnapshot((v) => !v)}
          style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "1rem 1.25rem", background: "transparent", border: "none", cursor: "pointer", color: "#fff" }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <i className="ri-dashboard-line" style={{ fontSize: 18, color: "var(--bl-accent-light)" }} />
            <span style={{ fontFamily: "var(--bl-font-display)", fontSize: "1.05rem", fontWeight: 600 }}>Overview</span>
            <span style={{ fontSize: 12, color: "var(--bl-text-soft)" }}>users · alerts</span>
          </span>
          <i className={`ri-arrow-down-s-line ${showSnapshot ? "" : ""}`} style={{ fontSize: 20, color: "var(--bl-text-soft)", transform: showSnapshot ? "rotate(180deg)" : "none", transition: "transform 160ms" }} />
        </button>
      </div>

      {showSnapshot && (
        <>
          {/* ── Top KPI row ───────────────────────────────────────────── */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "1rem",
            marginBottom: "1.5rem",
          }}>
            <KpiCard loading={loading} label="App Users"     value={fmt(appUsers)}       icon="ri-user-add-line"        accent="var(--bl-accent-light)" sub={appUsersSource === "posthog" ? "Verified · PostHog first-wallet events" : "Approx · first wallet pings (per install)"} />
            <KpiCard loading={loading} label="Card Users"    value={fmt(cardUsers)}      icon="ri-bank-card-line"       accent="var(--bl-accent-light)" sub="Have / started a card" />
            <KpiCard loading={loading} label="Active price alerts" value={fmt(activeAlerts)}   icon="ri-alarm-warning-line"   accent="var(--bl-accent)" sub="User crypto price watches" />
            <KpiCard loading={loading} label="Unique visitors"   value={fmt(uniqueVisitors)} icon="ri-eye-line"        accent="var(--bl-accent-light)" sub="biorlabs.com · all time" />
            <KpiCard
              loading={loading}
              label="Stuck pending sends"
              value={fmt(stuckPending.count)}
              icon={stuckPending.count > 0 ? "ri-error-warning-line" : "ri-checkbox-circle-line"}
              accent={stuckPending.count > 0 ? "#ff5c5c" : "var(--bl-success)"}
              accentRgb={stuckPending.count > 0 ? "255,92,92" : "0,205,82"}
              sub={stuckPending.count > 0
                ? `PENDING > ${stuckPending.threshold_min}m — may need reconciliation`
                : "No sends stuck in pending"}
            />
            {errorsAllowed && (
              <KpiCard
                loading={!errorsSummary}
                label="Open app errors"
                value={fmt(errorsSummary?.counts?.unresolved || 0)}
                sub={errorsSummary ? `${Number(errorsSummary?.counts?.last_24h || 0).toLocaleString()} in last 24h` : ""}
                icon="ri-error-warning-line"
                accent="var(--bl-success)"
              />
            )}
          </div>
        </>
      )}

      <style>{`@media (max-width: 768px) { .overview-grid { grid-template-columns: 1fr !important; } .northstar-grid { grid-template-columns: 1fr !important; } }`}</style>
    </div>
  );
};

export default Overview;
